/**
 * Small form controls styled to the inquiry form design: a label above, a
 * 50px white box with a hairline border, an optional gold icon on the left,
 * and a red message underneath when the field has an error.
 */
import { forwardRef } from "react";
import { motion } from "framer-motion";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ja from "date-fns/locale/ja";
import { useTranslation } from "react-i18next";
import { CalendarIcon, ClockIcon, SelectArrowIcon } from "../../../../components/icons";
import { PhoneInput } from "../../../../components/ui/Input";
import { cn } from "../../../../utils/classnames";
import { formatDisplayDate, formatPickedTime, parsePickedTime } from "../dates";
import { Fold, RadioDot, usePressMotion } from "./animated";

registerLocale("ja", ja);

/**
 * Bootstrap's reboot sets `font-size: inherit; line-height: inherit` on
 * button/input/select/textarea and `border-radius: 0` on button, outside
 * Tailwind's CSS layer -- so it beats plain `tw:` classes on those elements.
 * Size, line height and radius on form controls therefore carry `!`.
 */
const INPUT_TEXT_CLASS =
  "tw:font-hanken-grotesk tw:!text-[14px] tw:!leading-[20px] tw:lg:!text-[15px] tw:lg:!leading-[22px] tw:text-[#282828]";

/** The white 50px box every single-line control sits in. */
export const inputBoxClass = (hasError) =>
  cn(
    "tw:h-[50px] tw:w-full tw:bg-white tw:border tw:border-solid tw:!rounded-[5px] tw:transition-colors",
    hasError
      ? "tw:border-[#EA4335]"
      : "tw:border-[#E8E8E8] tw:focus-within:border-[#DBBE78] tw:hover:border-[#D9D9D9]"
  );

/** Label + optional hint line, with the red asterisk on required fields. */
export const FieldLabel = ({ htmlFor, label, hint, isRequired }) => (
  <div className="tw:flex tw:flex-col tw:gap-y-[3px] tw:font-hanken-grotesk">
    <label
      htmlFor={htmlFor}
      className="tw:!mb-0 tw:text-[14px] tw:leading-[20px] tw:lg:text-[15px] tw:lg:leading-[22px] tw:text-[#212529]"
    >
      {label}
      {isRequired && <span className="tw:text-[#EA4335]"> *</span>}
    </label>
    {hint && (
      <span className="tw:text-[12px] tw:leading-[17px] tw:lg:text-[13px] tw:lg:leading-[18.5px] tw:text-[rgba(78,78,78,0.87)]">
        {hint}
      </span>
    )}
  </div>
);

/**
 * Red message under a field. `errorKey` is a translation key, or empty.
 * It folds open below the field, and folds away once the field is fixed.
 */
export const FieldError = ({ id, errorKey }) => {
  const { t } = useTranslation();
  return (
    <Fold isOpen={Boolean(errorKey)}>
      <p id={id} role="alert" data-field-error="true" className="tw:!mb-0 tw:pt-[6px] tw:font-hanken-grotesk tw:text-[12px] tw:leading-[17px] tw:!text-[#EA4335]">
        {errorKey && t(`meetAssistInquiry.${errorKey}`)}
      </p>
    </Fold>
  );
};

/** Label on top, control in the middle, error at the bottom. */
export const Field = ({ id, label, hint, isRequired, errorKey, children, className }) => (
  <div className={cn("tw:flex tw:flex-col tw:gap-y-[10px] tw:min-w-0", className)}>
    {label && <FieldLabel htmlFor={id} label={label} hint={hint} isRequired={isRequired} />}
    <div>
      {children}
      <FieldError id={`${id}-error`} errorKey={errorKey} />
    </div>
  </div>
);

/** Single-line text input, optionally with an icon on the left. */
export const TextInput = ({ id, value, onChange, placeholder, icon, errorKey, type = "text", maxLength = 200 }) => (
  <div className={cn(inputBoxClass(Boolean(errorKey)), "tw:flex tw:items-center tw:gap-x-[10px] tw:px-[15px]")}>
    {icon && <span className="tw:shrink-0 tw:flex tw:items-center">{icon}</span>}
    <input
      id={id}
      type={type}
      value={value}
      maxLength={maxLength}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      aria-invalid={Boolean(errorKey)}
      aria-describedby={errorKey ? `${id}-error` : undefined}
      className={cn(
        INPUT_TEXT_CLASS,
        "tw:flex-1 tw:min-w-0 tw:h-full tw:bg-transparent tw:border-0 tw:outline-none tw:placeholder:text-[#8D8D8D]"
      )}
    />
  </div>
);

/**
 * Native <select> drawn like the design's dropdown: value on the left, gold
 * triangle on the right. Native keeps it keyboard- and phone-friendly.
 *
 * A native option cannot colour part of its text, so when nothing is chosen
 * the placeholder is drawn on top instead -- that is how "Age Needed *" gets
 * its red asterisk (`isPlaceholderRequired`).
 *
 * @param {Array<{ value: string, label: string }>} options
 */
export const SelectInput = ({ id, value, onChange, options, placeholder, isPlaceholderRequired, errorKey, className }) => (
  <div className={cn(inputBoxClass(Boolean(errorKey)), "tw:relative", className)}>
    {value === "" && placeholder && (
      <span
        aria-hidden="true"
        className={cn(INPUT_TEXT_CLASS, "tw:pointer-events-none tw:absolute tw:inset-y-0 tw:left-[15px] tw:flex tw:items-center tw:!text-[#4E4E4E]")}
      >
        {placeholder}
        {isPlaceholderRequired && <span className="tw:text-[#EA4335]">&nbsp;*</span>}
      </span>
    )}
    <select
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-invalid={Boolean(errorKey)}
      aria-describedby={errorKey ? `${id}-error` : undefined}
      className={cn(
        INPUT_TEXT_CLASS,
        "tw:appearance-none tw:w-full tw:h-full tw:bg-transparent tw:border-0 tw:outline-none tw:pl-[15px] tw:pr-[40px] tw:cursor-pointer",
        // Empty: the overlay above shows the placeholder, so hide the select's own text.
        value === "" ? "tw:!text-transparent" : "tw:!text-[#4E4E4E]",
        // The open list must stay readable even while the closed box hides its text.
        "tw:[&>option]:text-[#282828]"
      )}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <span className="tw:pointer-events-none tw:absolute tw:right-[15px] tw:top-1/2 tw:-translate-y-1/2 tw:flex">
      <SelectArrowIcon size={10} />
    </span>
  </div>
);

export const TextAreaInput = ({ id, value, onChange, placeholder, maxLength = 2000 }) => (
  <textarea
    id={id}
    value={value}
    maxLength={maxLength}
    placeholder={placeholder}
    onChange={(event) => onChange(event.target.value)}
    className={cn(
      INPUT_TEXT_CLASS,
      "tw:block tw:w-full tw:h-[120px] tw:resize-y tw:p-[15px] tw:bg-white tw:border tw:border-solid tw:border-[#E8E8E8] tw:!rounded-[5px] tw:outline-none tw:focus:border-[#DBBE78] tw:placeholder:text-[#A7A7A7]"
    )}
  />
);

/**
 * react-datepicker asks for one element to open the calendar from. It is the
 * same box as TextInput, but a button so phones do not pop up a keyboard.
 * The picker overwrites `placeholder` and `id` on its custom input, so the
 * empty-state text travels as `emptyText` and the id is given to the picker.
 * forwardRef is required: the picker keeps a ref to it to place the popup.
 */
const PickerTrigger = forwardRef(({ id, displayValue, emptyText, icon, errorKey, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    id={id}
    onClick={onClick}
    aria-describedby={errorKey ? `${id}-error` : undefined}
    className={cn(inputBoxClass(Boolean(errorKey)), "tw:flex tw:items-center tw:gap-x-[10px] tw:px-[15px] tw:text-left")}
  >
    <span className="tw:shrink-0 tw:flex tw:items-center">{icon}</span>
    <span className={cn(INPUT_TEXT_CLASS, !displayValue && "tw:!text-[#8D8D8D]")}>
      {displayValue || emptyText}
    </span>
  </button>
));
PickerTrigger.displayName = "PickerTrigger";

const getDatePickerLocale = (language) => (language === "en" ? "en" : "ja");

/** Calendar date picker. Past dates are disabled. `value` is a Date or null. */
export const DateInput = ({ id, value, onChange, placeholder, errorKey }) => {
  const { i18n } = useTranslation();
  return (
    <DatePicker
      id={id}
      selected={value}
      onChange={onChange}
      minDate={new Date()}
      locale={getDatePickerLocale(i18n.language)}
      wrapperClassName="tw:w-full"
      customInput={
        <PickerTrigger
          id={id}
          errorKey={errorKey}
          emptyText={placeholder}
          displayValue={formatDisplayDate(value, i18n.language === "en" ? "en" : "jp")}
          icon={<CalendarIcon color="#DBBE78" size={18} aria-hidden="true" />}
        />
      }
    />
  );
};

/** Time-only picker in 5-minute steps. `value` and `onChange` use "HH:mm". */
export const TimeInput = ({ id, value, onChange, placeholder, errorKey, caption }) => {
  const { i18n } = useTranslation();
  return (
    <DatePicker
      id={id}
      selected={parsePickedTime(value)}
      onChange={(pickedDate) => onChange(formatPickedTime(pickedDate))}
      showTimeSelect
      showTimeSelectOnly
      timeIntervals={5}
      timeFormat="HH:mm"
      timeCaption={caption}
      locale={getDatePickerLocale(i18n.language)}
      wrapperClassName="tw:w-full"
      customInput={
        <PickerTrigger
          id={id}
          errorKey={errorKey}
          emptyText={placeholder}
          displayValue={value}
          icon={<ClockIcon size={18} aria-hidden="true" />}
        />
      }
    />
  );
};

/**
 * Phone number with the country-code picker, reusing the site's PhoneInput.
 * `value` is `{ countryCode, dialCode, number }`.
 */
export const PhoneNumberInput = ({ id, value, onChange, placeholder, errorKey }) => (
  <PhoneInput
    name={id}
    placeholder={placeholder}
    value={value.number}
    countryCode={value.countryCode}
    dialCode={value.dialCode}
    fontSize="15px"
    isInvalid={Boolean(errorKey)}
    className="tw:!mb-0"
    onChange={(event) => onChange({ ...value, number: event.target.value })}
    onCountryChange={(countryCode, dialCode) => onChange({ ...value, countryCode, dialCode })}
  />
);

/**
 * The round radio mark used on every choice card: the ring darkens and the
 * dot pops in when chosen.
 */
export const RadioMark = ({ isSelected }) => (
  <span
    aria-hidden="true"
    className={cn(
      "tw:shrink-0 tw:size-4 tw:!rounded-full tw:flex tw:items-center tw:justify-center tw:border-solid tw:transition-colors tw:duration-200",
      isSelected ? "tw:border-2 tw:border-[#282828]" : "tw:border-[1.5px] tw:border-[#D9D9D9]"
    )}
  >
    <RadioDot isVisible={isSelected} />
  </span>
);

/**
 * Selected cards get a 1.5px dark edge. The extra half pixel is drawn as an
 * inset shadow on top of a 1px border, so choosing a card never nudges its
 * content -- and border colour and shadow can both fade smoothly.
 */
export const selectableCardClass = (isSelected, unselectedBorderClass = "tw:border-[#D9D9D9]") =>
  cn(
    "tw:bg-white tw:!rounded-[5px] tw:border tw:border-solid tw:transition-[border-color,box-shadow] tw:duration-200",
    isSelected
      ? "tw:border-[#282828] tw:shadow-[inset_0_0_0_0.5px_#282828]"
      : cn(unselectedBorderClass, "tw:hover:border-[#8D8D8D]")
  );

/**
 * A card-shaped radio button: dark 1.5px border when chosen, grey hairline
 * otherwise. Used for the pick up sign, car details and contact choices.
 */
export const ChoiceButton = ({ isSelected, onSelect, title, description, className }) => {
  const pressMotion = usePressMotion(1, 0.985);
  return (
    <motion.button
      {...pressMotion}
      type="button"
      role="radio"
      aria-checked={isSelected}
      onClick={onSelect}
      className={cn(
        selectableCardClass(isSelected),
        "tw:flex-1 tw:min-w-0 tw:flex tw:items-center tw:gap-x-3 tw:lg:gap-x-4 tw:text-left",
        description ? "tw:p-[18px]" : "tw:min-h-[50px] tw:px-[18px] tw:py-[14px]",
        className
      )}
    >
      <RadioMark isSelected={isSelected} />
      <span className="tw:flex tw:flex-col tw:gap-y-[2px] tw:font-hanken-grotesk tw:min-w-0">
        <span
          className={cn(
            "tw:text-[14px] tw:leading-[20px] tw:lg:text-[15px] tw:lg:leading-[22px] tw:text-[#282828]",
            isSelected && "tw:font-bold"
          )}
        >
          {title}
        </span>
        {description && (
          <span className="tw:text-[12px] tw:leading-[17px] tw:lg:text-[13px] tw:lg:leading-[18.5px] tw:text-[#4E4E4E]">
            {description}
          </span>
        )}
      </span>
    </motion.button>
  );
};

/** Two ChoiceButtons side by side on desktop, stacked on phones. */
export const ChoiceGroup = ({ label, isRequired, children }) => (
  <div className="tw:flex tw:flex-col tw:gap-y-[10px]">
    {label && <FieldLabel label={label} isRequired={isRequired} />}
    <div role="radiogroup" aria-label={label} className="tw:flex tw:flex-col tw:sm:flex-row tw:gap-[6px]">
      {children}
    </div>
  </div>
);
